import { gql } from "@apollo/client";
import { ExternalLinkIcon, ShieldCheckIcon } from "lucide-react";
import { FC } from "react";

import { Button } from "@src/lib/ui/Button";
import { Card } from "@src/lib/ui/Card";
import Text from "@src/lib/ui/Text";
import useUser from "@src/features/app/hooks/useUser";
import { AssignmentsInput } from "@src/types.generated";

import {
  useMyTrainingFoodHandlerExamsQuery,
  useMyTrainingFoodHandlerModulesQuery,
} from "./MyTrainingFoodHandlerExams.generated";

const MyTrainingFoodHandlerExams: FC = () => {
  const { user } = useUser();
  const { data: moduleData } = useMyTrainingFoodHandlerModulesQuery();
  const libraryItemIds =
    moduleData?.PremiumContentConfigs.objects.flatMap((config) =>
      config.catalogModule?.module.libraryItem.id
        ? [config.catalogModule.module.libraryItem.id]
        : [],
    ) ?? [];
  const input: AssignmentsInput = {
    filters: {
      userId: { value: user?.id ?? -1 },
      statuses: { value: ["incomplete", "past_due", "complete"] },
      libraryItemIds: { value: libraryItemIds },
      includeCompletedAssignmentsWithoutAccessSource: { value: true },
    },
    sort: {
      column: "currentInstanceLastAssignedAt",
      descending: true,
      nullsLast: true,
    },
  };
  const { data } = useMyTrainingFoodHandlerExamsQuery({
    variables: { input, pagination: { limit: 100 } },
    skip: !user || libraryItemIds.length === 0,
    pollInterval: 60_000,
  });
  const assignments = data?.Assignments.objects ?? [];
  const assignment =
    assignments.length > 0
      ? assignments.reduce((latest, candidate) =>
          (candidate.currentModuleInstance?.createdAt ?? "") >
          (latest.currentModuleInstance?.createdAt ?? "")
            ? candidate
            : latest,
        )
      : undefined;
  const moduleInstance = assignment?.currentModuleInstance;
  const exam = moduleInstance?.foodHandlerExam;

  if (!assignment || !moduleInstance?.completedAt || !exam || exam.passed) {
    return null;
  }

  const examName = assignment.libraryItem.name.myTranslation;

  return (
    <section
      aria-labelledby="certification-exams-heading"
      className="border-b border-border-primary bg-bg-secondary px-4 py-5 sm:px-6"
    >
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheckIcon className="size-5 text-text-secondary" />
        <div>
          <h2>
            <Text id="certification-exams-heading" variant="compact-title">
              Certification exams
            </Text>
          </h2>
          <Text variant="compact-description" className="text-text-secondary">
            Complete your exam to earn your certification.
          </Text>
        </div>
      </div>
      <Card
        variant="soft"
        className="flex flex-col gap-4 bg-bg-primary p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <Text variant="compact-title" className="truncate">
            {examName}
          </Text>
          <Text variant="compact-description" className="text-text-secondary">
            Training complete. Your certification exam is ready.
          </Text>
        </div>
        <Button asChild className="shrink-0">
          <a
            href={exam.link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Start ${examName} exam (opens in a new tab)`}
          >
            Start exam
            <ExternalLinkIcon data-icon="inline-end" />
          </a>
        </Button>
      </Card>
    </section>
  );
};

export default MyTrainingFoodHandlerExams;

gql`
  query MyTrainingFoodHandlerModules {
    PremiumContentConfigs(
      input: { complianceTypes: [FOOD_HANDLER, FOOD_MANAGER] }
    ) {
      objects {
        id
        catalogModule {
          id
          module {
            id
            libraryItem {
              id
            }
          }
        }
      }
    }
  }

  query MyTrainingFoodHandlerExams(
    $input: AssignmentsInput!
    $pagination: PaginationInput
  ) {
    Assignments(input: $input, pagination: $pagination) {
      objects {
        id
        libraryItem {
          id
          name {
            ...MyTranslationSet
          }
        }
        currentModuleInstance {
          id
          createdAt
          completedAt
          foodHandlerExam {
            id
            passed
            link
          }
        }
      }
    }
  }
`;
